import { CanvaSX } from './canvasx';
import { FlexColumn } from './components/FlexColumn';
import { FlexRow } from './components/FlexRow';
import { Image } from './components/Image';
import { Rect } from './components/Rect';
import { Style } from './components/Style';
import { Text } from './components/Text';
import {
  borderSide,
  borderSideVertical,
  isBorderStruct,
  isFillStruct,
  normalizeSpacing,
} from './shapes';
import { measureText } from './textRendering';

export function isElement(node: CanvaSXNode): node is CanvaSXElement {
  return !!node && typeof node === 'object' && 'props' in node;
}

export function isElementOfType<T>(
  node: CanvaSXNode,
  type: T
): node is CanvaSXElement<
  T extends CanvaSXComponent<infer Props> ? Props : never
> {
  return isElement(node) && node.type === type;
}

export function canvasCssValue(name: string, fallback: string) {
  const { _canvasComputedStyle, _cssVariables } = CanvaSX.context;
  if (!(name in _cssVariables)) {
    _cssVariables[name] = _canvasComputedStyle?.getPropertyValue(name) ?? '';
  }

  return _cssVariables[name] || fallback;
}

export function parseColorValue(value: string, fallback: string) {
  const match = /^var\((--[^)]+)\)/.exec(value);
  if (match) {
    return canvasCssValue(match[1], fallback);
  }

  return value;
}

export function calculateDimensionsForProps(
  props: ElementProps & Partial<ShapeStruct>
): DimensionStruct {
  const { width, height } = props;

  const margin = normalizeSpacing(props.margin);
  const padding = normalizeSpacing(props.padding);

  const dimensions: DimensionStruct = { width: 0, height: 0 };

  dimensions.width += margin.left + margin.right;
  dimensions.width += padding.left + padding.right;

  dimensions.height += margin.top + margin.bottom;
  dimensions.height += padding.top + padding.bottom;

  let childDimensions: DimensionStruct;
  if (typeof width === 'number') {
    dimensions.width += width;
  } else {
    childDimensions = calculateDimensionsForNode(props.children);

    dimensions.width += childDimensions.width;
    props.width = childDimensions.width + padding.left + padding.right;
  }

  if (typeof height === 'number') {
    dimensions.height += height;
  } else {
    childDimensions ||= calculateDimensionsForNode(props.children);
    dimensions.height += childDimensions.height;
    props.height = childDimensions.height + padding.top + padding.bottom;
  }

  return dimensions;
}

export function calculateDimensionsForChildren(
  children: CanvaSXNode[]
): DimensionStruct {
  return children
    .map(_node => calculateDimensionsForNode(_node))
    .reduce(
      (sum, dimension) => {
        sum.width += dimension.width;
        sum.height = Math.max(dimension.height, sum.height);
        return sum;
      },
      { width: 0, height: 0 }
    );
}

const InternalComponents = [FlexColumn, FlexRow, Rect, Text, Style, Image];

export function isInternalComponent(el: CanvaSXElement) {
  return InternalComponents.includes(el.type);
}

export let measuringElement = false;

export function calculateDimensionsForElement(
  element: CanvaSXElement,
  recalculate = false
): DimensionStruct {
  if (recalculate) {
    delete element.props._dimensions;

    if (element.type.recalculateDimensions) {
      return (element.props._dimensions = element.type.recalculateDimensions(
        element.props
      ));
    }
  }

  if (
    isElement(element) &&
    typeof element.props.width === 'number' &&
    typeof element.props.height === 'number'
  ) {
    return { width: element.props.width, height: element.props.height };
  } else if (element.type.calculateDimensions) {
    return (element.props._dimensions ||= element.type.calculateDimensions(
      element.props
    ));
  } else if (InternalComponents.includes(element.type)) {
    return calculateDimensionsForProps(element.props);
  } else {
    // This is a custom component that is returning a JSX element, we need to "render" it to measure it
    const prevMeasuringElement = measuringElement;
    try {
      measuringElement = true;
      const content = element.type(element.props) as JSX.Element | null;
      measuringElement = prevMeasuringElement;

      if (content) {
        const dimensions = calculateDimensionsForElement(content);
        element.props._dimensions = dimensions;
        return dimensions;
      } else {
        return { width: 0, height: 0 };
      }
    } finally {
      measuringElement = prevMeasuringElement;
    }

    // eslint-disable-next-line no-unreachable
    return { width: 0, height: 0 };
  }
}

export function calculateDimensionsForNode(
  node: CanvaSXNode | CanvaSXNode[] | undefined
): DimensionStruct {
  const { ctx } = CanvaSX.context;

  if (Array.isArray(node)) {
    return calculateDimensionsForChildren(node);
  }

  if (node === null) return { width: 0, height: 0 };

  switch (typeof node) {
    case 'string':
    case 'number':
      return measureText(ctx, String(node));

    case 'boolean':
    case 'undefined':
      return { width: 0, height: 0 };

    case 'object':
      return calculateDimensionsForElement(node);

    default:
      console.error('unhandled node type', node);
      break;
  }

  return { width: 0, height: 0 };
}

export function drawRect(
  ctx: CanvasRenderingContext2D,
  shape: ShapeStruct & BorderStruct,
  fn?: () => void
) {
  const {
    x = 0,
    y = 0,
    border,
    fill,
    borderRadius = 0,
    opacity = 1,
    clip,
  } = shape;

  const margin = normalizeSpacing(shape.margin);
  const padding = normalizeSpacing(shape.padding);

  ctx.save();

  if (clip) {
    drawClip(ctx, shape);
  }

  ctx.translate(x + margin.left, y + margin.top);

  if (fill) {
    ctx.save();
    ctx.globalAlpha = opacity;

    if (isFillStruct(fill)) {
      ctx.fillStyle = fill.color ? fill.color : 'black';
      ctx.globalAlpha = fill.opacity ?? 1;
    } else {
      ctx.fillStyle = fill;
    }

    drawBorder(ctx, shape, borderRadius, 'fill');
    ctx.restore();
  }

  if (border) {
    ctx.save();
    const borderOpacity = isBorderStruct(border)
      ? border.opacity ?? opacity
      : opacity;
    ctx.globalAlpha = borderOpacity;

    if (isBorderStruct(border)) {
      const side = borderSide(border);

      if (!side) {
        ctx.strokeStyle = border.color ? border.color : 'black';
        ctx.lineWidth = border.lineWidth ?? 1;
        drawBorder(ctx, shape, borderRadius, 'stroke');
      } else {
        drawBorderStruct(ctx, shape, border, side);
      }
    } else if (typeof border === 'string') {
      ctx.strokeStyle = border;
      drawBorder(ctx, shape, borderRadius, 'stroke');
    }
    ctx.restore();
  }

  if (fn) {
    ctx.translate(padding.left, padding.top);
    fn();
  }

  ctx.restore();
}

function drawBorderStruct(
  ctx: CanvasRenderingContext2D,
  shape: ShapeStruct & BorderStruct,
  border: BorderStruct,
  side: keyof BorderSidesStruct
) {
  const { width, height, lineWidth = 1 } = shape;
  const { color, lineWidth: borderWidth, opacity, size: borderSize } = border;

  ctx.lineWidth = borderWidth ?? lineWidth;
  ctx.globalAlpha = opacity ?? 1;

  // side on which to draw this border
  const vertical = borderSideVertical(side);
  // Percentage of the side the border will be drawn on
  const size =
    typeof borderSize === 'number'
      ? borderSize
      : parseInt(borderSize ?? '100%', 10) / 100;
  const dimension = vertical ? height : width;
  const borderDim = typeof borderSize === 'number' ? size : dimension * size;
  const offset = (dimension - borderDim) / 2;
  ctx.strokeStyle = border[side] ?? color ?? 'black';

  // Draw a line on the specified side of the size determined by offset
  // Also, adjust the position of the border by linewidth so the border is always
  // inside the shape
  ctx.beginPath();
  switch (side) {
    case 'left':
      ctx.moveTo(lineWidth, offset);
      ctx.lineTo(lineWidth, height - offset);
      break;
    case 'right':
      ctx.moveTo(width, offset);
      ctx.lineTo(width, height - offset);
      break;
    case 'top':
      ctx.moveTo(offset, lineWidth);
      ctx.lineTo(width - offset, lineWidth);
      break;
    case 'bottom':
      ctx.moveTo(offset, height - lineWidth);
      ctx.lineTo(width - offset, height - lineWidth);
      break;
  }

  ctx.stroke();
}

function drawBorder(
  ctx: CanvasRenderingContext2D,
  shape: ShapeStruct,
  borderRadius: number | BorderRadiusStruct,
  renderType: 'stroke' | 'fill'
) {
  let { width, height, border } = shape;
  let lineWidth = 0;
  let x = 0;
  let y = 0;

  if (shouldOffsetFill(shape, renderType)) {
    if (isBorderStruct(border)) {
      lineWidth = border.lineWidth ?? 1;
    }
    x += lineWidth / 2;
    y += lineWidth / 2;
    width -= lineWidth;
    height -= lineWidth;
  }

  if (!borderRadius || typeof borderRadius === 'number') {
    if (borderRadius > 0 && ctx.roundRect) {
      ctx.beginPath();
      const radius =
        renderType === 'fill' ? borderRadius - lineWidth / 2 : borderRadius;
      ctx.roundRect(x, y, width, height, radius);
      ctx[renderType]();
    } else {
      renderType === 'stroke'
        ? ctx.strokeRect(x, y, width, height)
        : ctx.fillRect(x, y, width, height);
    }
  } else {
    const {
      topLeft = 0,
      topRight = 0,
      bottomLeft = 0,
      bottomRight = 0,
    } = borderRadius;

    ctx.beginPath();
    ctx.moveTo(topLeft, 0);
    ctx.arcTo(width, 0, width, height, topRight);
    ctx.arcTo(width, height, 0, height, bottomRight);
    ctx.arcTo(0, height, 0, 0, bottomLeft);
    ctx.arcTo(0, 0, width, 0, topLeft);
    ctx.closePath();
    ctx[renderType]();
  }
}

function drawClip(ctx: CanvasRenderingContext2D, shape: ShapeStruct) {
  const { clip } = shape;
  if (clip) {
    const {
      x,
      y,
      width,
      height,
      fillRule = 'nonzero',
      borderRadius = 0,
    } = clip;

    ctx.beginPath();
    // if evenodd, the clip dimensions represent the area we want to be clipped
    // out of this shape
    if (fillRule === 'evenodd') {
      ctx.rect(0, 0, shape.width, shape.height);
    }
    if (borderRadius && ctx.roundRect) {
      ctx.roundRect(x, y, width, height, borderRadius);
    } else {
      ctx.rect(x, y, width, height);
    }
    ctx.clip(fillRule);
  }
}

export function drawSeparator({
  ctx,
  width: objectWidth,
  height: objectHeight,
  separator,
  border,
}: {
  ctx: CanvasRenderingContext2D;
  separator: SeparatorStruct;
  border: ShapeStyle['border'] | undefined;
} & DimensionStruct) {
  // Make separator span object in direction of layout
  const { color = 'black', opacity = 1, direction, lineWidth = 1 } = separator;
  const isVertical = direction === 'vertical';

  // If we have a transparent border, act as if there is no border
  let borderWidth = 0;
  if (isBorderStruct(border)) {
    const side = borderSide(border);
    const color = side ? border[side] : border.color;
    if (color !== 'transparent') {
      borderWidth = border.lineWidth ?? 1;
    }
  } else if (border && border !== 'transparent') {
    borderWidth = 1;
  }

  // Get actual size of separator
  const width = isVertical ? lineWidth : objectWidth - borderWidth;
  const height = isVertical ? objectHeight - borderWidth : lineWidth;
  // offset separator by border width
  const x = isVertical ? 0 : borderWidth / 2;
  const y = isVertical ? borderWidth / 2 : 0;

  drawRect(ctx, {
    x,
    y,
    width,
    height,
    opacity,
    fill: { color, opacity: separator.opacity },
  });

  // Ensure subsequent nodes aren't drawn over separator
  ctx.translate(isVertical ? lineWidth : 0, isVertical ? 0 : lineWidth);
}

export const isSafari =
  navigator.vendor?.includes('Apple') &&
  !navigator.userAgent?.includes('CriOS') &&
  !navigator.userAgent?.includes('FxiOS') &&
  !navigator.userAgent?.includes('jsdom');

// When filling a rect, if the border is not opaque avoid overlap. Overlap causes:
// - The border to not be fully visible (if only border is opaque)
// - Dark edges where non-opaque border overlaps with non-opaque fill
function shouldOffsetFill(shape: ShapeStruct, renderType: 'stroke' | 'fill') {
  const { border } = shape;
  const borderStruct = isBorderStruct(border);
  // If border is opaque, no need to offset
  if (!borderStruct) return false;
  // Offset happens with fill
  if (renderType === 'stroke') return false;

  // Only offset if the border is not opaque
  return (border.opacity ?? 1) < 1;
}

export const getFlexGrowValue = (
  propValue: CanvaSXIntrinsicProps['flexGrow']
): number => {
  if (propValue) {
    // here flexGrow is either `true` or a non-zero number.
    // `true` is treated as `flexGrow={1}`
    return typeof propValue === 'number' ? propValue : 1;
  }
  return 0;
};
